import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import CountryCodeSelectionField from '../../components/CountryCodeSelection/CountryCodeSelectionField';
import KycCheckboxField from '../../components/KycCheckboxField/KycCheckboxField';
import MultipleCheckboxField from '../../components/MultipleCheckboxField/MultipleCheckboxField';
import RadioField from '../../components/RadioField/RadioField';
import Select from '../../components/SelectField/SelectField';
import TextInputField from '../../components/TextInputField/TextInputField';
import ValidationErrorMessage from '../../components/ValidationErrorMessage/ValidationErrorMessage';
import useDefaultCitizenship from '../../hooks/useDefaultCitizenship';
import useTranslation from '../../hooks/useTranslation';
import { CITIZENSHIP_GROUP_ID, isSupportedQuestionType, Kyc, KycQuestionTypes, Question } from '../../types/KycTypes';

type Props = {
    kycData: Kyc;
    question: Question;
};

/**
 * The stored answer is the option label rather than its id, because that is what the answer POST expects.
 */
const toFieldOptions = (question: Question) =>
    question.options?.map((option) => ({
        id: option.label,
        label: option.label,
    })) || [];

/**
 * One report per unknown type per session. A template we cannot render affects every question of that type on every
 * render, and the actionable signal is the type itself, so repeating it would only bury it.
 */
const reportedUnsupportedTypes = new Set<string>();

const KycQuestion = ({ kycData, question }: Props) => {
    const translate = useTranslation();
    const name = 'KYC_' + question.groupId + '_' + question.name.toString();
    const countryOptions = kycData?.kycCountries?.map((option) => ({ id: option.id, label: option.label })) || [];
    const isSupported = isSupportedQuestionType(question.type);

    useDefaultCitizenship(
        name,
        question.groupId === CITIZENSHIP_GROUP_ID && question.type === KycQuestionTypes.COUNTRY_CODE_SELECTION,
        kycData?.kycCountries,
    );

    useEffect(() => {
        if (isSupported || reportedUnsupportedTypes.has(question.type)) {
            return;
        }

        reportedUnsupportedTypes.add(question.type);
        Sentry.captureException(new Error(`Unsupported KYC question type: ${question.type}`), {
            tags: { source: 'kyc_question' },
            extra: { questionName: question.name, groupId: question.groupId, type: question.type },
        });
    }, [isSupported, question.type, question.name, question.groupId]);

    switch (question.type) {
        case KycQuestionTypes.SINGLE_SELECTION_DROPDOWN:
            return <Select name={name} label={question.label} options={toFieldOptions(question)} />;

        case KycQuestionTypes.SINGLE_SELECTION:
            return <RadioField name={name} options={toFieldOptions(question)} />;

        case KycQuestionTypes.FREE_TEXT:
            return <TextInputField name={name} label={question.label} />;

        case KycQuestionTypes.CHECK_BOX:
            return <KycCheckboxField name={name} label={question.label} options={toFieldOptions(question)} />;

        case KycQuestionTypes.MULTIPLE_SELECTION:
            return <MultipleCheckboxField name={name} options={toFieldOptions(question)} />;

        // Both country types render the same multi-select field, which is what COUNTRY_CODE_SELECTION already used
        // before MULTIPLE_COUNTRY_CODE_SELECTION existed. Holding the single-country type to one selection is a
        // separate question for the market owners.
        case KycQuestionTypes.COUNTRY_CODE_SELECTION:
        case KycQuestionTypes.MULTIPLE_COUNTRY_CODE_SELECTION:
            return <CountryCodeSelectionField name={name} label={question.label} options={countryOptions} />;

        case KycQuestionTypes.BUTTON:
            return <RadioField name={name} options={toFieldOptions(question)} />;

        default:
            // Never render nothing here. An unknown type used to fall through to a RadioField with no options, which
            // is a required question the customer cannot answer: validation then blocks "next" forever with nothing
            // on screen to fix. Saying so out loud is worse UX than a working field and far better than a dead end.
            //
            // The title, not the description: the description is a whole-page dead end ("take me back to
            // resursbank.no") and carries an anchor tag, which would show as raw markup here. The page itself renders
            // the full message once the customer tries to continue.
            return <ValidationErrorMessage>{translate('page.error.title')}</ValidationErrorMessage>;
    }
};

export default KycQuestion;
